/**
 * ship-sprites.ts — v4M4.a — per-(race, chassis) commissioned bitmap
 * sprite cache + loader.
 *
 * Mirrors the v4M1.8a backdrop pattern (`backdrop.ts`):
 *   1. A `Map<key, OffscreenCanvas>` cache keyed by `'<RaceId>:<chassis>'`.
 *   2. `loadShipSpriteImage(race, chassis, url)` fetches an
 *      `HTMLImageElement`, paints into a 128×128 OffscreenCanvas, and
 *      replaces the cache entry. Headless-safe + non-rejecting per the
 *      backdrop pattern — a missing file leaves the cache empty so the
 *      consumer's procedural fallback stays active.
 *   3. `getShipSprite(race, chassis)` is the cache read. Returns null
 *      when no sprite is loaded for the key.
 *
 * The render pipeline (`ship-draw.ts`) checks `getShipSprite(...)` for
 * each ship; if non-null it blits the bitmap (rotated by heading,
 * scaled by class) instead of running the procedural per-race
 * silhouette draw. Procedural draws remain in `ship-draw.ts` so the
 * game ships fully without commissioned sprites — Alpha 0.5 visual
 * read is preserved verbatim until sprite assets land in
 * `public/assets/ships/`.
 *
 * Sprite authoring contract (per `GRAPHICS.md §4.2` — to be amended
 * when v4M4.c lands commissioned ships):
 *   - 128×128 PNG, RGBA, transparent background.
 *   - Ship oriented NOSE-UP (-y direction in canvas coordinates).
 *   - Ship centred at (64, 64); fits within an inscribed 96 px circle
 *     so the v2M7.2.5 class-scale factor (0.75× — 2.0×) doesn't clip
 *     against the canvas edge at maximum class.
 *   - Per-race silhouette identity per GRAPHICS §4.3 — Terran steel
 *     blue / Kargath blood red / Reman stealth violet etc.; the
 *     render layer no longer recolours the sprite (no empire-accent
 *     tint applied to bitmap output — Empire.color drives selection
 *     ring + halo only).
 */

import type { RaceId } from '../state/types/index';

/**
 * Canonical sprite dimensions. The 128 px tile fits comfortably inside
 * the procedural `SHIP_DRAW_RADIUS = 7 px` × class-scale up to 2.0×
 * after a 0.11× render scale (so the bitmap renders at ~14 px screen
 * size at class 2, matching the procedural silhouette extent).
 */
const SHIP_SPRITE_SIZE = 128;

/**
 * Render scale applied at blit time. v4M4.a initial value was 0.11
 * (read at ~14 px at class 2 / 1× scale, matching the procedural
 * silhouette extent). v4M4.c first-playtest signal (2026-05-15)
 * showed 14 px too small for the commissioned art's internal detail
 * to register — class 1-3 ships read as ambiguous blobs. Bumped to
 * 0.20.
 *
 * MFix14 second-playtest signal (2026-05-24) showed 0.20 still too
 * small for the per-color-ring ownership marker to read clearly
 * during combat. Bumped to 0.30 (1.5× larger again). Class 1 now
 * ~28 px (Scout prow + engine pods + accent ring readable); class 4
 * ~57 px (capital-ship presence); class 6 ~77 px (mega-capital
 * dominance). The earlier "0.25 cap before capitals dominate
 * planets" comment was conservative — playtest shows capitals
 * benefit from feeling massive, not blending with planet glyphs.
 *
 * The class 1-to-6 size ratio remains 2.67× via the unchanged
 * classScale (0.75× → 2.0×), so the size hierarchy still reads.
 *
 * This constant governs the bitmap path only. Procedural per-race
 * silhouettes in `ship-draw.ts` use hardcoded path coordinates
 * hand-tuned for the 14 px footprint and are unaffected. After this
 * bump, commissioned-art races visibly out-scale not-yet-commissioned
 * races' procedural placeholders — desirable signal that the upgrade
 * has landed.
 *
 * Further iteration: nudge by 0.02 increments if playtest signal
 * indicates ships are still too small to read (towards 0.35 or 0.40)
 * or too large (back towards 0.25).
 */
export const SHIP_SPRITE_RENDER_SCALE = 0.3;

/** Cache key `<RaceId>:<chassis>` → painted offscreen canvas. */
const shipSpriteCache: Map<string, OffscreenCanvas> = new Map();

/**
 * MFix14 — per-color sprite variant cache. Keyed by
 * `<RaceId>:<chassis>:<colorId>` where `colorId` is the palette ID
 * matching `EMPIRE_COLOR_PALETTE` in `src/state/empire-colors.ts`.
 * Populated by `tryLoadCommissionedShipColorVariants` at app boot;
 * read by `getColoredShipSprite` from the renderer's bitmap fast
 * path. Distinct from `tintedSpriteCache` — colored variants are
 * pre-authored by the artist (Option C, baked accent ring); the
 * tinted cache is the runtime multiply-tint fallback (Option B).
 */
const coloredSpriteCache: Map<string, OffscreenCanvas> = new Map();

/**
 * MFix14-prep — empire-tinted sprite cache. Keyed by
 * `<RaceId>:<chassis>:<empireColor>` so each (race, chassis, owner-
 * colour) combination resolves a single multiply-tinted copy. Built
 * lazily on first request from the corresponding raw sprite, then
 * cached for the rest of the session. With 3 races × 13 chassis × up
 * to 6 empire colours = ~234 entries; negligible memory at 128×128.
 */
const tintedSpriteCache: Map<string, OffscreenCanvas> = new Map();

function cacheKey(race: RaceId, chassis: string): string {
  return `${race}:${chassis}`;
}

function tintedKey(race: RaceId, chassis: string, color: string): string {
  return `${race}:${chassis}:${color}`;
}

function coloredKey(race: RaceId, chassis: string, colorId: string): string {
  return `${race}:${chassis}:${colorId}`;
}

/**
 * Return the cached sprite for the given (race, chassis) pair, or null
 * when no sprite has been loaded for it. Consumers (`ship-draw.ts`)
 * branch on this — null falls through to the procedural silhouette.
 */
export function getShipSprite(
  race: RaceId,
  chassis: string,
): OffscreenCanvas | null {
  return shipSpriteCache.get(cacheKey(race, chassis)) ?? null;
}

/**
 * MFix14-prep — return a sprite multiply-tinted by the empire's accent
 * colour. Lazily builds and caches the tinted variant on first request.
 *
 * Multiply blend semantics: each source pixel's RGB is multiplied
 * componentwise by the empire colour. Bright source areas pick up the
 * empire tint; dark areas stay dark; the JPG-black background tints
 * to a darker shade of the empire colour but stays invisible against
 * the system-view dark backdrop. Works cleanly with neutral-grey
 * source sprites; source sprites with strong existing accents (red
 * lights, green glow) will fight the tint and look muddy — design
 * call surfaced in the source-sprite scan.
 *
 * Returns null when the raw sprite isn't loaded yet (consumer falls
 * back to the procedural silhouette per the existing contract).
 */
export function getTintedShipSprite(
  race: RaceId,
  chassis: string,
  empireColor: string,
): OffscreenCanvas | null {
  const raw = shipSpriteCache.get(cacheKey(race, chassis));
  if (!raw) return null;
  const key = tintedKey(race, chassis, empireColor);
  const cached = tintedSpriteCache.get(key);
  if (cached) return cached;
  if (typeof OffscreenCanvas === 'undefined') return raw; // headless fallback
  const out = new OffscreenCanvas(raw.width, raw.height);
  const ctx = out.getContext('2d');
  if (!ctx) return raw;
  // Paint the raw sprite, then overlay the empire colour with
  // `multiply` so bright source pixels take the tint.
  ctx.drawImage(raw, 0, 0);
  ctx.globalCompositeOperation = 'multiply';
  ctx.fillStyle = empireColor;
  ctx.fillRect(0, 0, raw.width, raw.height);
  // Multiply's alpha-compositing math (`Sa + Da × (1 − Sa)`) turns
  // transparent destination pixels OPAQUE under an opaque source
  // fillRect — so the transparent background of an RGBA sprite would
  // become a solid empire-colour square. Re-apply the sprite's alpha
  // channel as a mask via `destination-in` to restore transparency
  // outside the silhouette.
  ctx.globalCompositeOperation = 'destination-in';
  ctx.drawImage(raw, 0, 0);
  // Restore default composite for any downstream paints that might
  // reuse the context (defensive — OffscreenCanvas ctx is local here
  // but the habit prevents accidental leakage if someone refactors).
  ctx.globalCompositeOperation = 'source-over';
  tintedSpriteCache.set(key, out);
  return out;
}

/**
 * MFix14 — return the pre-authored color variant of a sprite for the
 * given (race, chassis, empireColor) triple, or null when the variant
 * isn't loaded. The renderer's bitmap fast path consults this first;
 * a null return falls through to `getTintedShipSprite` (Option B
 * multiply tint on the neutral sprite), then to the procedural
 * silhouette. The colorId is resolved from `paletteIdFromHex` against
 * the empire's hex colour; empires whose colour isn't in the palette
 * (legacy saves carrying a free-RGB v4M1.9 colour) get null from
 * the lookup and fall through.
 */
export function getColoredShipSprite(
  race: RaceId,
  chassis: string,
  colorId: string,
): OffscreenCanvas | null {
  return coloredSpriteCache.get(coloredKey(race, chassis, colorId)) ?? null;
}

/**
 * MFix14 — load a per-color sprite variant into the colored cache.
 * Same headless-safe + non-rejecting contract as
 * `loadShipSpriteImage`. URL is the runtime probe path
 * `/assets/ships/<race-slug>-<chassis-slug>-<color-id>.png`
 * built by the caller (`main.ts`).
 */
export function loadColoredShipSpriteImage(
  race: RaceId,
  chassis: string,
  colorId: string,
  url: string,
): Promise<void> {
  return new Promise((resolve) => {
    if (
      typeof OffscreenCanvas === 'undefined' ||
      typeof Image === 'undefined'
    ) {
      resolve();
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = new OffscreenCanvas(SHIP_SPRITE_SIZE, SHIP_SPRITE_SIZE);
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve();
          return;
        }
        ctx.clearRect(0, 0, SHIP_SPRITE_SIZE, SHIP_SPRITE_SIZE);
        ctx.drawImage(img, 0, 0, SHIP_SPRITE_SIZE, SHIP_SPRITE_SIZE);
        coloredSpriteCache.set(coloredKey(race, chassis, colorId), canvas);
      } catch {
        /* leave cache untouched — fallback chain stays intact */
      }
      resolve();
    };
    img.onerror = () => resolve();
    img.src = url;
  });
}

/**
 * v4M4.a — load a commissioned ship sprite for the given (race,
 * chassis). Painted into a 128×128 OffscreenCanvas regardless of
 * source dimensions; commissioned sprites should be authored at
 * exactly 128×128.
 *
 * Promise always resolves (no rejection): a failed load leaves the
 * existing cache entry untouched, the procedural fallback stays
 * active, and other in-flight loads in the batch continue.
 * Headless-safe: when `OffscreenCanvas` or `Image` is undefined the
 * load is a no-op.
 */
export function loadShipSpriteImage(
  race: RaceId,
  chassis: string,
  url: string,
): Promise<void> {
  return new Promise((resolve) => {
    if (
      typeof OffscreenCanvas === 'undefined' ||
      typeof Image === 'undefined'
    ) {
      resolve();
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = new OffscreenCanvas(SHIP_SPRITE_SIZE, SHIP_SPRITE_SIZE);
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve();
          return;
        }
        ctx.clearRect(0, 0, SHIP_SPRITE_SIZE, SHIP_SPRITE_SIZE);
        ctx.drawImage(img, 0, 0, SHIP_SPRITE_SIZE, SHIP_SPRITE_SIZE);
        shipSpriteCache.set(cacheKey(race, chassis), canvas);
      } catch {
        /* leave cache untouched — procedural fallback stays active */
      }
      resolve();
    };
    img.onerror = () => resolve();
    img.src = url;
  });
}

/**
 * Test-only — clear the sprite cache. Called from unit tests so that a
 * load in one test doesn't leak into the next.
 */
export function _resetShipSpriteCacheForTests(): void {
  shipSpriteCache.clear();
  tintedSpriteCache.clear();
  coloredSpriteCache.clear();
}
